import { Button } from "@components/ui/button"
import { Field } from "@components/ui/form/Field"
import { Link } from "@components/ui/link"
import { ModalOrSidebar } from "@components/ui/modal-sidebar"
import { Text } from "@components/ui/typograhpy/Text"
import { getGraphqlClient } from "@lib/graphql-client"
import { extractErrorCode, parseError } from "@lib/parse-error"
import { useDispatch } from "@lib/store"
import { useMutation } from "@tanstack/react-query"
import { Form, Formik } from "formik"
import React, { FC, useState } from "react"

type FormValues = {
  question: string
  requestRepresentative: boolean
  phone?: string
}
const { sdk } = getGraphqlClient()

type QuoteModalProps = {
  isActivePlan: boolean
}

export const QuoteModal: FC<QuoteModalProps> = ({ isActivePlan }) => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const [error, setError] = useState<undefined | string>(undefined)
  const contactSupport = useMutation({
    mutationFn: async ({
      question,
      requestRepresentative,
      phone,
    }: FormValues) => {
      await sdk.ContactSupport({
        input: {
          type: "Request Enterprise plan quote",
          question,
          requestsRepresentative: requestRepresentative,
          phoneNumber: phone ? phone : "",
        },
      })
    },
  })

  return (
    <ModalOrSidebar
      open={open}
      onOpenChange={setOpen}
      title="Get a quote"
      trigger={
        <Button
          variant="secondary"
          className="min-h-[unset] h-fit! py-2.5 px-5 mt-6 w-full md:mt-0 md:w-fit"
          leftIcon="contact"
        >
          {isActivePlan ? "Current plan" : "Reach out for a quote"}
        </Button>
      }
    >
      <Text variant="regular" className="mb-5">
        {`We’re happy to get back to you on our Enterprise plan! Could you tell us
        something about your needs and wishes?`}
      </Text>
      <Formik
        initialValues={{
          question: "",
          requestRepresentative: false,
        }}
        validateOnChange={true}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <Field
              autoFocus
              name="question"
              field="textarea"
              label="Type your message"
              className="mb-8"
              validation={{
                required: true,
              }}
            />
            <Field
              className="mb-8"
              name="requestRepresentative"
              id="requestRepresentative"
              field="checkbox"
              label="I would like to talk to a Viesus representative"
            />
            {values.requestRepresentative && (
              <Field
                name="phone"
                field="input"
                type="phone"
                label="Phone number"
                validation={{
                  required: true,
                  phone: true,
                }}
              />
            )}
            {error && (
              <Text variant="regular" className="text-danger mb-8">
                {error}
              </Text>
            )}
            <div className="flex justify-between items-center mb-8">
              <Link as="button" onClick={() => setOpen((val) => !val)}>
                Cancel
              </Link>
              <Button variant="primary" status="idle" type="submit">
                Send message
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalOrSidebar>
  )

  async function handleSubmit(formValues: FormValues) {
    try {
      await contactSupport.mutateAsync({
        ...formValues,
      })
      setError(undefined)
      setOpen(false)
      dispatch({
        type: "add-toast",
        payload: {
          variant: "success",
          title:
            "Support request successfully sent. We will get back to you as soon as possible.",
        },
      })
      setOpen(false)
    } catch (error) {
      setError(parseError(extractErrorCode(error)))
      if (process.env.NODE === "development") {
        console.error(error)
      }
    }
  }
}
